import React, { useState, useEffect } from 'react';
import { Course, Lesson, CourseLevel } from '../../types';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Alert,
  Spinner,
  Modal,
  Tabs,
  Tab
} from 'react-bootstrap';
import {
  FaPlay,
  FaEdit,
  FaTrash,
  FaFile,
  FaArrowLeft,
  FaPlus,
  FaVideo
} from 'react-icons/fa';
import LessonsList from './LessonsList';
import { addLessonToCourse, updateLessonInfo as updateLesson, deleteLessonFromCourse as deleteLesson } from '../../services/courseService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

interface CourseDetailsProps {
  course: Course;
  lessons: Lesson[];
  loading?: boolean;
  error?: string | null;
  onBack: () => void;
  onEditCourse: (course: Course) => void;
  onDeleteCourse: (courseId: string) => Promise<void>;
  onEditLesson: (lesson: Lesson) => void;
  onDeleteLesson: (lessonId: string) => Promise<void>;
}

const CourseDetails: React.FC<CourseDetailsProps> = ({
  course,
  lessons,
  loading = false,
  error = null,
  onBack,
  onEditCourse,
  onDeleteCourse,
  onEditLesson,
  onDeleteLesson
}) => {
  const { currentUser } = useAuth();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [lessonsState, setLessonsState] = useState<Lesson[]>(lessons);

  useEffect(() => {
    setLessonsState(lessons);
  }, [lessons]);

  const handleDeleteCourse = async () => {
    try {
      await onDeleteCourse(course.id);
    } catch (error) {
      setDeleteError('حدث خطأ أثناء حذف الكورس');
      console.error('Error deleting course:', error);
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    try {
      await onDeleteLesson(lessonId);
      setLessonsState(prevLessons => prevLessons.filter(lesson => lesson.id !== lessonId));
    } catch (error) {
      setDeleteError('حدث خطأ أثناء حذف الدرس');
      console.error('Error deleting lesson:', error);
    }
  };

  const handleAddLesson = async (lessonData: Partial<Lesson>) => {
    try {
      if (!currentUser) {
        throw new Error('يجب تسجيل الدخول لإضافة درس');
      }

      const lessonId = await addLessonToCourse(
        {
          ...lessonData,
          courseId: course.id,
          order: lessonsState.length + 1,
          isPublished: false
        },
        currentUser.uid
      );
      
      const newLesson: Lesson = {
        id: lessonId,
        courseId: course.id,
        title: lessonData.title || '',
        description: lessonData.description || '',
        videoUrl: lessonData.videoUrl || '',
        order: lessonsState.length + 1,
        duration: lessonData.duration || 0,
        isPublished: false,
        attachments: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      setLessonsState([...lessonsState, newLesson]);
      
      // إخطار المستخدم بنجاح العملية
      toast.success('تم إضافة الدرس بنجاح');
    } catch (error) {
      console.error('Error adding lesson:', error);
      toast.error('حدث خطأ أثناء إضافة الدرس');
    }
  };

  const handleUpdateLesson = async (lessonId: string, lessonData: Partial<Lesson>) => {
    try {
      await updateLesson(lessonId, lessonData);
      
      setLessonsState(prevLessons => 
        prevLessons.map(lesson => 
          lesson.id === lessonId ? { ...lesson, ...lessonData, updatedAt: Date.now() } : lesson
        )
      );
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating lesson:', error);
      return Promise.reject(error);
    }
  };

  const getLevelText = (level?: string) => {
    switch (level) {
      case 'beginner':
        return 'مبتدئ';
      case 'intermediate':
        return 'متوسط';
      case 'advanced':
        return 'متقدم';
      default:
        return 'غير محدد';
    }
  };

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" />
        <p className="mt-2">جاري تحميل تفاصيل الكورس...</p>
      </div>
    );
  }

  return (
    <Container className="py-4">
      {error && <Alert variant="danger">{error}</Alert>}
      {deleteError && <Alert variant="danger">{deleteError}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="link" onClick={onBack} className="text-decoration-none">
          <FaArrowLeft className="me-2" />
          العودة إلى قائمة الكورسات
        </Button>
        <div>
          <Button
            variant="warning"
            className="me-2"
            onClick={() => onEditCourse(course)}
          >
            <FaEdit className="me-1" />
            تعديل الكورس
          </Button>
          <Button variant="danger" onClick={handleDeleteCourse}>
            <FaTrash className="me-1" />
            حذف الكورس
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              {course.imageUrl && (
                <img
                  src={course.imageUrl}
                  alt={course.title}
                  className="img-fluid rounded mb-3"
                />
              )}
            </Col>
            <Col md={8}>
              <h2 className="mb-3">{course.title}</h2>
              <p className="text-muted">{course.description}</p>

              <Row className="mt-4">
                <Col md={6}>
                  <div className="mb-3">
                    <h5>المستوى</h5>
                    <p>{getLevelText(course.level)}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h5>المادة</h5>
                    <p>{course.subject || 'غير محدد'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h5>السعر</h5>
                    <p>{course.price} ج.م</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h5>المدة</h5>
                    <p>{course.duration || 0} ساعة</p>
                  </div>
                </Col>
              </Row>

              <div className="mt-3">
                <Badge bg={course.isPublished ? 'success' : 'warning'} className="me-2">
                  {course.isPublished ? 'منشور' : 'مسودة'}
                </Badge>
                <Badge bg="info" className="me-2">
                  {lessonsState.length} درس
                </Badge>
                <Badge bg="primary">
                  {course.enrolledCount || 0} طالب مسجل
                </Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || 'details')}
        className="mb-4"
      >
        <Tab eventKey="details" title="تفاصيل الكورس">
          <div className="mt-4">
            <h3 className="mb-3">الدروس</h3>
            {lessonsState.length === 0 ? (
              <Alert variant="info">لا توجد دروس في هذا الكورس بعد.</Alert>
            ) : (
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>عنوان الدرس</th>
                    <th>الوصف</th>
                    <th>المدة (دقيقة)</th>
                    <th>الحالة</th>
                    <th>المرفقات</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {lessonsState.map((lesson, index) => (
                    <tr key={lesson.id}>
                      <td>{index + 1}</td>
                      <td>{lesson.title}</td>
                      <td>{lesson.description || 'لا يوجد وصف'}</td>
                      <td>{lesson.duration || 0}</td>
                      <td>
                        {lesson.isPublished ? (
                          <Badge bg="success">منشور</Badge>
                        ) : (
                          <Badge bg="warning">مسودة</Badge>
                        )}
                      </td>
                      <td>
                        {lesson.attachments?.map((attachment, i) => (
                          <Button
                            key={i}
                            variant="link"
                            size="sm"
                            href={attachment.url}
                            target="_blank"
                            className="me-2"
                          >
                            <FaFile className="me-1" />
                            {attachment.name}
                          </Button>
                        ))}
                      </td>
                      <td>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => onEditLesson(lesson)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteLesson(lesson.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Tab>
        <Tab eventKey="lessons" title="إدارة الحصص والفيديوهات">
          <div className="mt-4">
            <LessonsList
              courseId={course.id}
              lessons={lessonsState}
              onLessonAdded={handleAddLesson}
              onLessonUpdated={handleUpdateLesson}
              onLessonDeleted={handleDeleteLesson}
            />
          </div>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default CourseDetails;
